import React, { useState } from 'react';

/**
 * Pro Analyst Dynasty governing document.
 *
 * Mechanical rules were reconciled against Sleeper league
 * 1312241670577737728 on October 7, 2026. League policy is intentionally
 * separated from platform configuration so members can tell which provisions
 * Sleeper enforces automatically and which require commissioner enforcement.
 */

const ARTICLES = [
  {
    id: 'dynasty-art-1',
    num: 'I',
    title: 'League Identity & Authority',
    body: (
      <>
        <h4>1. League</h4>
        <p><strong>Pro Analyst Dynasty</strong> is a ten-team, year-round dynasty fantasy football league hosted on Sleeper.</p>

        <h4>2. Official Records & Communication</h4>
        <ul>
          <li><strong>Sleeper</strong> is authoritative for rosters, scoring, transactions, draft picks, standings, and matchup results.</li>
          <li><strong>League Discord</strong> is the official channel for notices, votes, disputes, deadlines, and commissioner rulings.</li>
          <li><strong>The League Intel By-Laws page</strong> is the official published copy of these rules. A dated revision on this page constitutes written notice; a separate Discord announcement is a courtesy unless a rule expressly requires one.</li>
          <li>League Intel analysis explains league data and policy but may not silently override Sleeper or the published by-laws.</li>
        </ul>

        <h4>3. Equal Application</h4>
        <p>These rules apply equally to every franchise, including the commissioner's team. Enforcement must rely on documented conduct and verifiable league data—not record, reputation, personal conflict, or assumptions about intent.</p>
      </>
    ),
  },
  {
    id: 'dynasty-art-2',
    num: 'II',
    title: 'Format, Scoring & Schedule',
    sleeper: true,
    body: (
      <>
        <h4>1. Competitive Format</h4>
        <ul>
          <li><strong>10 teams</strong> with permanent dynasty rosters.</li>
          <li><strong>Full PPR</strong> scoring with an additional <strong>0.75 points per tight-end reception</strong>.</li>
          <li><strong>6-point passing touchdowns</strong> and <strong>SuperFlex</strong> lineup construction.</li>
          <li><strong>4 playoff teams</strong>; playoffs begin in Week 16.</li>
        </ul>

        <h4>2. Notable Scoring Bonuses</h4>
        <ul>
          <li>0.3 points per rushing or receiving first down.</li>
          <li>Passing bonuses at 300 and 400 yards; rushing/receiving bonuses at 100 and 200 yards.</li>
          <li>Long-play and long-touchdown bonuses are applied as configured in Sleeper.</li>
        </ul>
        <p className="dynasty-bylaws-note">Sleeper's live scoring settings control stat corrections and final matchup totals. A displayed League Intel projection is advisory, not an official score.</p>
      </>
    ),
  },
  {
    id: 'dynasty-art-3',
    num: 'III',
    title: 'Rosters, IR & Taxi Squad',
    sleeper: true,
    body: (
      <>
        <h4>1. Starting Lineup</h4>
        <div className="dynasty-bylaws-lineup" aria-label="Dynasty starting lineup">
          {['1 QB', '2 RB', '3 WR', '1 TE', '2 FLEX', '1 SUPERFLEX', '1 K', '1 DEF'].map(slot => <span key={slot}>{slot}</span>)}
        </div>
        <p>Each franchise also has <strong>11 bench positions</strong>, <strong>2 IR positions</strong>, and <strong>3 taxi positions</strong>.</p>

        <h4>2. Injured Reserve</h4>
        <p>IR eligibility follows Sleeper. Players designated Out or on reserve-eligible lists may be placed on IR; doubtful and suspended players are not IR-eligible under the current configuration.</p>

        <h4>3. Taxi Squad</h4>
        <ul>
          <li>Taxi eligibility is limited to players within their first NFL season; veterans are not eligible.</li>
          <li>The taxi squad locks after the Week 4 deadline shown in Sleeper.</li>
          <li>Promotions, demotions, and eligibility are governed by Sleeper's live controls.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'dynasty-art-4',
    num: 'IV',
    title: 'Waivers, Free Agency & FAAB',
    sleeper: true,
    body: (
      <>
        <h4>1. Seasonal Budget</h4>
        <p>Each franchise begins the season with <strong>$100 FAAB</strong>. Zero-dollar bids are permitted. Unused FAAB has no cash value and does not carry into a new league year unless the league expressly votes otherwise.</p>

        <h4>2. Processing</h4>
        <p>Waiver timing, player lock status, and tie-breaking are controlled by Sleeper. Managers are responsible for entering claims before the platform deadline and for arranging conditional claims in the desired priority order.</p>

        <h4>3. Competitive Participation</h4>
        <p>No manager is required to make a minimum number of claims, adds, drops, or trades. Transaction volume alone is not proof of abandonment. Management is judged by legal lineups, timely injury response, and documented communication under Article VII.</p>
      </>
    ),
  },
  {
    id: 'dynasty-art-5',
    num: 'V',
    title: 'Rookie Draft, Draft Picks & Trades',
    sleeper: true,
    body: (
      <>
        <h4>1. Rookie Draft</h4>
        <ul>
          <li>The annual rookie draft is <strong>three rounds</strong>.</li>
          <li>Draft position is determined by <strong>reverse regular-season Max Points For (Max PF)</strong>: the franchise with the lowest Max PF receives the first pick, the next-lowest receives the second pick, and so on through the highest Max PF.</li>
          <li>The same order applies in every rookie-draft round. This is a linear draft, not a snake draft.</li>
          <li>Max PF means the potential points recorded by Sleeper from a franchise's roster. Benching a productive player therefore does not improve that franchise's draft position.</li>
          <li>Sleeper's final Max PF totals, including official stat corrections, control the order. Any exact tie is resolved by the applicable Sleeper tiebreaker or, if Sleeper does not resolve it, a commissioner ruling published before the draft.</li>
        </ul>

        <h4>2. Draft-Pick Trading</h4>
        <p>Future draft-pick trading is enabled. A manager may trade only picks the platform recognizes as owned by that franchise. Conditional terms must be written in Discord and acknowledged by every involved manager before acceptance.</p>

        <h4>3. Trades</h4>
        <ul>
          <li>Two-team and multi-team trades are permitted.</li>
          <li>Accepted trades process immediately under the current Sleeper setting.</li>
          <li>A bad, unpopular, or uneven trade is not automatically collusion. Reversal requires evidence of collusion, roster dumping, rule evasion, or an accidental platform action promptly reported by an involved manager.</li>
          <li>Sleeper stores a <strong>5-vote veto threshold</strong>, but trades currently process immediately and automatic veto polling is not enabled. The setting does not create a routine pre-processing review window. Any commissioner reversal must be explained publicly with the controlling rule and evidence.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'dynasty-art-6',
    num: 'VI',
    title: 'Competitive Integrity',
    body: (
      <>
        <h4>1. Good-Faith Competition</h4>
        <p>Every manager must make a good-faith effort to maintain a legal, competitive roster throughout the entire season, regardless of record, rebuild status, draft position, or playoff elimination.</p>

        <h4>2. Rebuilding Is Permitted</h4>
        <p>Managers may rebuild, trade veterans, accumulate picks, and accept short-term lineup weakness. A legitimate rebuild does not excuse empty starting positions, preventable inactive starters, collusion, or disengagement from commissioner outreach.</p>

        <h4>3. Evidence Standard</h4>
        <p>League Intel may surface patterns—unchanged lineups, low transaction activity, missed projections, or zero-point outcomes—for review. Those signals support an inquiry but do not become management strikes unless they satisfy Article VII §2. An officially inactive non-K/DST starter may separately count under Article VII §1 even when the stricter management-strike standard is not met.</p>
      </>
    ),
  },
  {
    id: 'dynasty-art-7',
    num: 'VII',
    title: 'Active Management & Franchise Intervention',
    body: (
      <>
        <div className="dynasty-bylaws-alert">
          <strong>Purpose</strong>
          <span>This ladder corrects disengagement while protecting managers from punishment based on an unlucky score or subjective roster opinion.</span>
        </div>

        <h4>1. Established Inactive-Player Lineup Rule</h4>
        <ul>
          <li>For purposes of the automatic-removal threshold, each franchise may accumulate up to <strong>twelve (12) inactive-player lineup violations per season</strong>. This allowance does not shield a preventable incident from the separate management-strike ladder.</li>
          <li>A violation occurs when a non-kicker, non-defense player in a starting lineup is <strong>officially designated inactive at kickoff</strong> of that player's NFL game.</li>
          <li>Each inactive starter is one violation; multiple inactive starters in the same scoring week are counted separately.</li>
          <li><strong>Kickers (K) and Defense/Special Teams (D/ST) are excluded</strong> from this inactive-player total.</li>
          <li>Beginning with a manager's <strong>thirteenth (13th) violation in the same season</strong>, removal from the league is automatic and the replacement process begins.</li>
          <li>The commissioner may waive an inactive-player violation only for a platform error or other clearly documented extenuating circumstance.</li>
        </ul>
        <p className="dynasty-bylaws-note"><strong>Counting standard:</strong> official inactive status at kickoff controls. A player who is active but scores zero does not count. An inactive-player violation does not automatically become a management strike; the stricter preventability standard below must also be satisfied.</p>

        <h4>How the Two Standards Work Together</h4>
        <ul>
          <li><strong>Active player scores zero:</strong> neither an inactive-player violation nor a management strike.</li>
          <li><strong>Surprise inactive at kickoff:</strong> counts toward the seasonal inactive-player total, but is not a management strike unless the preventability requirements are established.</li>
          <li><strong>Known Out/inactive before lock with an eligible replacement:</strong> counts toward the seasonal inactive-player total if the player is officially inactive at kickoff and may also be one management strike.</li>
          <li><strong>K or D/ST:</strong> excluded from the inactive-player total; an empty starting slot may still qualify as a management strike.</li>
        </ul>

        <h4>2. Verified Management Strike</h4>
        <p>A strike may be recorded only when documented evidence establishes one of the following:</p>
        <ol>
          <li>An <strong>empty starting slot</strong> remained at the applicable player or lineup lock.</li>
          <li>A player <strong>confirmed Out or inactive before lock</strong> remained in the starting lineup while an eligible replacement was available on that manager's roster.</li>
          <li>The manager failed to respond to a <strong>formal commissioner roster-management notice within 72 hours</strong>.</li>
        </ol>
        <p className="dynasty-bylaws-note"><strong>Not a strike by itself:</strong> an active player scoring zero, a low projection, an unchanged but legal lineup, declining a trade, rebuilding, conserving FAAB, or making few transactions.</p>

        <h4>3. Member-Requested Management Review</h4>
        <ul>
          <li>Any active league member may ask the commissioner to review another franchise's management. The request must identify the franchise, the week or pattern at issue, and the specific competitive-integrity concern.</li>
          <li>A request may be made publicly in league Discord or privately to the commissioner. <strong>The request itself is not evidence, a strike, or a presumption of wrongdoing.</strong></li>
          <li>The commissioner may examine Sleeper lineups, player status at lock, eligible roster replacements, transactions, response history, and relevant multi-season patterns.</li>
          <li>If the review identifies a credible concern, the commissioner may publish an evidence report to the league. The report must distinguish verified facts from inference, include meaningful counter-evidence and data limitations, and give the affected manager an opportunity to respond.</li>
          <li>Private messages or personal information unrelated to league management may not be published without the sender's permission.</li>
          <li>Repeated bad-faith, retaliatory, or knowingly false review requests may be treated as a conduct issue under Article I.</li>
        </ul>

        <h4>4. Formal Warning & Review</h4>
        <ul>
          <li>Documented multi-week or multi-season concern may trigger a written warning and a <strong>two-scoring-week management review</strong>.</li>
          <li>The manager must acknowledge the warning within <strong>48 hours</strong>, set a complete legal lineup before the first weekly kickoff, recheck it by <strong>11:00 AM ET Sunday</strong>, and address confirmed inactive players before their lock times.</li>
          <li>The commissioner must preserve the evidence for every alleged strike and give the manager a reasonable opportunity to explain platform, emergency, or timing issues.</li>
        </ul>

        <h4>5. Temporary Co-Manager</h4>
        <p><strong>Two verified management strikes during the review, or failure to acknowledge the warning within 48 hours,</strong> triggers a three-week temporary co-manager proposal. The commissioner will name the proposed co-manager and disclose the scope to the league.</p>
        <ul>
          <li>The co-manager has <strong>lineup-only authority</strong>.</li>
          <li>No trades, draft-pick transactions, FAAB transfers, or drops of core roster assets may occur without the franchise manager's and commissioner's approval.</li>
          <li>The intervention ends after three compliant scoring weeks unless a new verified strike occurs.</li>
        </ul>

        <h4>6. Replacement & Automatic Removal</h4>
        <p><strong>Three verified management strikes in any rolling six scoring weeks, or one new management strike after completing a temporary co-manager period,</strong> triggers a formal replacement review. The affected manager may respond before the vote and does not vote on their own removal. Replacement under the management-strike ladder requires a two-thirds vote of the remaining eligible managers.</p>
        <p><strong>The thirteenth inactive-player lineup violation in one season is a separate, automatic-removal threshold.</strong> Once the commissioner verifies the count and resolves any timely waiver request, no additional removal vote is required.</p>

        <h4>7. Effective Dates & Non-Retroactivity</h4>
        <p>The inactive-player rule was adopted on <strong>March 28, 2026</strong> and governs the 2026 season. Documented instances satisfying §1 on or after that date count toward the 2026 total; a zero-point result is never a substitute for proof of official inactive status at kickoff. The separate management-strike ladder is effective <strong>October 7, 2026</strong> and management strikes may not be assigned for conduct before that date. Every count must satisfy its applicable definition and be documented.</p>
      </>
    ),
  },
  {
    id: 'dynasty-art-8',
    num: 'VIII',
    title: 'Commissioner Rulings, Appeals & Amendments',
    body: (
      <>
        <h4>1. Commissioner Administration</h4>
        <p>The commissioner interprets and administers these by-laws, resolves platform issues, preserves evidence, and may take proportionate action expressly authorized here. The commissioner may not create a new penalty after an incident and apply it retroactively.</p>

        <h4>2. Appeals</h4>
        <p>A manager may appeal a management strike or ordinary ruling within 72 hours by identifying the disputed fact or rule. A manager may challenge an inactive-player count within 72 hours by disputing the official status/count or requesting the limited waiver authorized by Article VII §1. Automatic removal at the 13th count is paused until a timely challenge is resolved. The commissioner will publish the relevant league evidence and decision.</p>
        <p>A majority of uninvolved managers may overturn an ordinary commissioner ruling. That vote may not waive or change the established 13-inactive automatic-removal threshold; changing that threshold requires an amendment. Management-strike replacement votes follow Article VII §6.</p>

        <h4>3. Amendments</h4>
        <p>The provisions and effective dates published in this consolidated document control. Future material rule changes require dated written notice on this page, a reasonable discussion period, and a league vote. Unless necessary to correct an emergency or platform conflict, future competitive rule changes take effect in the next league year. Clerical corrections, contradiction repairs that do not change the controlling outcome, and updates that merely mirror an existing Sleeper setting may be made immediately and logged below.</p>
      </>
    ),
  },
  {
    id: 'dynasty-art-9-proposed',
    num: 'IX',
    title: 'Proposed Member Removal Procedure',
    proposed: true,
    body: (
      <>
        <div className="dynasty-bylaws-proposal-status">
          <strong>Proposed for the 2027 league year · Pending league approval</strong>
          <span>This article is published for offseason review only. It is not enforceable during the 2026 season and becomes effective no earlier than the first day of the 2027 league year after approval under Article VIII.</span>
        </div>

        <h4>1. Removal Is a Last Resort</h4>
        <p>Permanent removal is reserved for conduct that materially threatens league operation, fairness, safety, or continuity. When correction remains reasonably possible, the league should use a warning, corrective plan, probation, or temporary co-manager before permanent removal.</p>

        <h4>2. Grounds for Review</h4>
        <p>A removal case may proceed only when the written request alleges at least one of the following and identifies supporting evidence:</p>
        <ul>
          <li>Sustained failure to manage a franchise after documented notice and a reasonable opportunity to correct the behavior.</li>
          <li>Collusion, intentional roster dumping, concealed side compensation, or deliberate circumvention of league rules.</li>
          <li>Repeated harassment, threats, discriminatory abuse, or other serious conduct that makes continued league participation unsafe or unworkable.</li>
          <li>Failure to pay required league dues after the published deadline, written notice, and any applicable cure period.</li>
          <li>Deliberate falsification of league evidence, tampering with league systems, or misuse of commissioner or co-manager access.</li>
        </ul>
        <p className="dynasty-bylaws-note"><strong>Not grounds by themselves:</strong> losing, rebuilding, unconventional strategy, low transaction volume, rejecting trades, an unpopular or uneven trade, an active player scoring zero, projection disagreement, personality conflict, or criticism of league leadership.</p>

        <h4>3. Who May Request Review</h4>
        <p>Any active league member or the commissioner may submit a written removal-review request. It must identify the manager, controlling rule, dates or scoring weeks involved, requested outcome, and evidence believed to support review. A request is an allegation—not proof, a strike, or a removal vote.</p>

        <h4>4. Initial Screening</h4>
        <ul>
          <li>The commissioner determines within 72 hours whether the request states a recognized ground and includes enough verifiable information to investigate.</li>
          <li>Unsupported, retaliatory, repetitive, or purely subjective requests are dismissed in writing.</li>
          <li>If the commissioner is the subject, petitioner, material witness, or otherwise conflicted, an uninvolved manager selected by majority vote administers the case.</li>
        </ul>

        <h4>5. Evidence Packet</h4>
        <p>A case that passes screening receives a dated evidence packet containing the allegation, applicable rules, Sleeper records, relevant timestamped communications, prior notices or corrective opportunities, the manager's response, meaningful counter-evidence, and known data limitations. Rumor, anonymous accusation without corroboration, projections, and subjective roster-value opinions are not sufficient evidence.</p>
        <p>The decision standard is <strong>clear and convincing league evidence</strong>: reliable, documented information that makes the alleged ground highly probable. The burden remains on the party requesting removal.</p>

        <h4>6. Notice, Response &amp; Discussion</h4>
        <ol>
          <li>The affected manager receives the complete league-relevant evidence and written notice of the possible outcome.</li>
          <li>The manager has <strong>seven calendar days</strong> to respond, correct factual errors, provide counter-evidence, or propose a corrective plan.</li>
          <li>After the response window, the evidence packet and response are shared with eligible voters for a <strong>72-hour discussion period</strong>.</li>
          <li>Unrelated private information is redacted. A manager's silence may be documented but does not relieve voters of the evidence standard.</li>
        </ol>

        <h4>7. Interim Protection</h4>
        <p>Before a final vote, the commissioner or neutral administrator may impose only the least restrictive temporary measure needed to protect the league, such as suspending trades or assigning lineup-only assistance. No permanent player drop, draft-pick transfer, or roster liquidation may occur.</p>

        <h4>8. Removal Vote</h4>
        <ul>
          <li>The affected manager does not vote.</li>
          <li>Removal requires approval from <strong>75% of all eligible, non-recused managers, rounded up</strong>. Abstentions do not reduce the number of affirmative votes required.</li>
          <li>Each vote and the final count are recorded. Ballot secrecy may be used, but the result, threshold, and controlling findings must be published.</li>
          <li>If removal fails, the same allegations may not be resubmitted without material new evidence or a new incident.</li>
        </ul>

        <h4>9. Limited Reconsideration</h4>
        <p>Within 72 hours of the result, the affected manager may request reconsideration based only on material new evidence or a specific procedural error. If a majority of eligible voters grants reconsideration, the evidence is corrected and a new vote uses the same 75% threshold.</p>

        <h4>10. Effect of Removal</h4>
        <p>Upon approved removal, the commissioner begins a documented replacement search. The franchise roster, FAAB, and draft picks remain attached to the franchise and may not be redistributed. Financial obligations or refunds follow the league's existing dues policy.</p>

        <h4>11. Relationship to Existing Rules</h4>
        <p>If approved, this article governs discretionary permanent removal of a Dynasty member. It does not replace the separately established automatic-removal threshold for a 13th inactive-player violation unless the league separately amends Article VII. Removing a person from the commissioner role is distinct from removing that person's franchise membership.</p>
      </>
    ),
  },
];

const CHANGELOG = [
  {
    date: '2026-10-07',
    text: 'Consistency audit completed: clarified how the inactive-player count and management-strike ladder interact; fixed co-manager and replacement timing; aligned appeals, automatic removal, effective dates, trade processing, and amendment notice.',
  },
  {
    date: '2026-10-07',
    text: 'Article VII reconciled with the March 28, 2026 inactive-player lineup rule: 12 seasonal instances permitted; the 13th triggers automatic removal; K and D/ST are excluded; platform-error and documented-extenuating-circumstance waivers preserved.',
  },
  {
    date: '2026-10-07',
    text: 'Initial Dynasty By-Laws published from live Sleeper settings. Added the evidence-based management review, temporary co-manager, and replacement ladder in Article VII.',
  },
];

export default function DynastyBylaws() {
  const [openLog, setOpenLog] = useState(false);

  return (
    <div className="dynasty-bylaws-page">
      <style>{DYNASTY_BYLAWS_CSS}</style>

      <div className="page-header dynasty-bylaws-header">
        <div>
          <div className="page-title">📜 League By-Laws</div>
          <div className="dynasty-bylaws-meta">Pro Analyst Dynasty · Official Governing Document</div>
        </div>
        <span className="dynasty-bylaws-status">Reconciled with Sleeper</span>
      </div>

      <div className="card dynasty-bylaws-hero">
        <div className="dynasty-bylaws-hero-mark">PA</div>
        <div>
          <strong>Dynasty rules should be clear before they are needed.</strong>
          <p>Platform-controlled settings are mirrored from Sleeper. Governance provisions explain the evidence, notice, review, and voting required before commissioner intervention. Last reconciled October 7, 2026.</p>
        </div>
      </div>

      <nav className="card dynasty-bylaws-toc" aria-label="By-laws articles">
        <div className="dynasty-bylaws-toc-title">Articles</div>
        <div className="dynasty-bylaws-toc-links">
          {ARTICLES.map(article => (
            <a key={article.id} href={`#${article.id}`} className="dynasty-bylaws-toc-link">
              <span>{article.num}</span>{article.title}
            </a>
          ))}
        </div>
      </nav>

      {ARTICLES.map(article => (
        <section key={article.id} id={article.id} className="card dynasty-bylaws-article">
          <h2>
            <span>Article {article.num}</span>
            {article.title}
            {article.sleeper && <em>Sleeper-governed</em>}
            {article.proposed && <em className="dynasty-bylaws-proposed-tag">2027 proposal</em>}
          </h2>
          <div className="dynasty-bylaws-body">{article.body}</div>
        </section>
      ))}

      <section className="card dynasty-bylaws-article">
        <button className="dynasty-bylaws-log-toggle" onClick={() => setOpenLog(open => !open)}>
          {openLog ? '▲' : '▼'} Revision History
        </button>
        {openLog && (
          <ul className="dynasty-bylaws-changelog">
            {CHANGELOG.map(change => (
              <li key={`${change.date}-${change.text}`}>
                <span>{change.date}</span>
                <p>{change.text}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const DYNASTY_BYLAWS_CSS = `
.dynasty-bylaws-page { max-width: 920px; margin: 0 auto; }
.dynasty-bylaws-header { align-items: flex-end; }
.dynasty-bylaws-meta { margin-top: 8px; color: var(--text3); font: 0.68rem var(--font-mono); letter-spacing: 0.55px; text-transform: uppercase; }
.dynasty-bylaws-status { padding: 6px 9px; border: 1px solid rgba(var(--accent-rgb), .22); border-radius: 999px; color: var(--accent); background: rgba(var(--accent-rgb), .07); font: 0.66rem var(--font-mono); text-transform: uppercase; }
.dynasty-bylaws-hero { display: grid; grid-template-columns: 58px 1fr; gap: 16px; align-items: center; border-color: rgba(var(--accent-rgb), .2); background: linear-gradient(135deg, rgba(var(--accent-rgb), .095), var(--surface-card-start) 48%, var(--surface-card-end)); }
.dynasty-bylaws-hero-mark { display: grid; place-items: center; width: 54px; height: 54px; border: 1px solid rgba(var(--accent-rgb), .36); border-radius: 13px; color: var(--accent); background: rgba(var(--accent-rgb), .1); font: 1.35rem var(--font-display); letter-spacing: 1px; }
.dynasty-bylaws-hero strong { color: var(--text); font-size: 1rem; }
.dynasty-bylaws-hero p { margin: 7px 0 0; color: var(--text2); font-size: .9rem; line-height: 1.65; }
.dynasty-bylaws-toc { margin: 16px 0; }
.dynasty-bylaws-toc-title { margin-bottom: 10px; color: var(--text3); font: .7rem var(--font-mono); letter-spacing: 1px; text-transform: uppercase; }
.dynasty-bylaws-toc-links { display: flex; flex-wrap: wrap; gap: 8px; }
.dynasty-bylaws-toc-link { display: inline-flex; align-items: center; gap: 8px; padding: 7px 11px; border: 1px solid rgba(var(--accent-rgb), .15); border-radius: 8px; color: var(--text2); background: rgba(var(--accent-rgb), .055); font-size: .82rem; text-decoration: none; transition: .15s ease; }
.dynasty-bylaws-toc-link:hover { color: var(--text); background: rgba(var(--accent-rgb), .13); border-color: rgba(var(--accent-rgb), .3); }
.dynasty-bylaws-toc-link span { display: grid; place-items: center; min-width: 20px; height: 20px; padding: 0 4px; border-radius: 5px; color: var(--accent); background: rgba(var(--accent-rgb), .15); font: 700 .68rem var(--font-mono); }
.dynasty-bylaws-article { margin: 16px 0; scroll-margin-top: 20px; }
.dynasty-bylaws-article h2 { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin: 0 0 15px; padding-bottom: 12px; border-bottom: 1px solid var(--border); color: var(--text); font: 1.15rem var(--font-display); letter-spacing: .35px; }
.dynasty-bylaws-article h2 > span { padding: 4px 8px; border: 1px solid rgba(var(--accent-rgb), .2); border-radius: 6px; color: var(--accent); background: rgba(var(--accent-rgb), .08); font: .7rem var(--font-mono); letter-spacing: 1px; text-transform: uppercase; }
.dynasty-bylaws-article h2 > em { margin-left: auto; padding: 3px 7px; border: 1px solid var(--border); border-radius: 5px; color: var(--text3); font: normal .61rem var(--font-mono); letter-spacing: .5px; text-transform: uppercase; }
.dynasty-bylaws-article h2 > em.dynasty-bylaws-proposed-tag { color: var(--yellow); border-color: rgba(255, 211, 42, .3); background: rgba(255, 211, 42, .06); }
.dynasty-bylaws-body { color: var(--text2); font-size: .91rem; line-height: 1.65; }
.dynasty-bylaws-body h4 { margin: 19px 0 7px; color: var(--text); font-size: .95rem; }
.dynasty-bylaws-body h4:first-child { margin-top: 0; }
.dynasty-bylaws-body p { margin: 8px 0; }
.dynasty-bylaws-body ul, .dynasty-bylaws-body ol { margin: 8px 0; padding-left: 23px; }
.dynasty-bylaws-body li { margin: 6px 0; }
.dynasty-bylaws-body strong { color: var(--text); }
.dynasty-bylaws-note { margin: 11px 0 !important; padding: 11px 13px; border: 1px solid rgba(var(--accent-rgb), .18); border-radius: 8px; background: rgba(var(--accent-rgb), .06); font-size: .86rem; }
.dynasty-bylaws-lineup { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0; }
.dynasty-bylaws-lineup span { padding: 6px 10px; border: 1px solid rgba(var(--accent-rgb), .2); border-radius: 7px; color: var(--text); background: rgba(var(--accent-rgb), .07); font: .79rem var(--font-mono); }
.dynasty-bylaws-alert { display: grid; gap: 5px; margin-bottom: 18px; padding: 13px 14px; border-left: 3px solid var(--yellow); border-radius: 8px; background: rgba(255, 211, 42, .055); }
.dynasty-bylaws-alert strong { color: var(--yellow); font: .73rem var(--font-mono); letter-spacing: .8px; text-transform: uppercase; }
.dynasty-bylaws-alert span { color: var(--text2); font-size: .86rem; line-height: 1.55; }
.dynasty-bylaws-proposal-status { display: grid; gap: 6px; margin-bottom: 18px; padding: 14px; border: 1px solid rgba(255, 211, 42, .28); border-left: 4px solid var(--yellow); border-radius: 9px; background: rgba(255, 211, 42, .055); }
.dynasty-bylaws-proposal-status strong { color: var(--yellow); font: .72rem var(--font-mono); letter-spacing: .7px; text-transform: uppercase; }
.dynasty-bylaws-proposal-status span { color: var(--text2); font-size: .86rem; line-height: 1.55; }
.dynasty-bylaws-log-toggle { padding: 0; border: 0; color: var(--text); background: none; cursor: pointer; font: 1.05rem var(--font-display); }
.dynasty-bylaws-changelog { margin: 14px 0 0; padding: 0; list-style: none; }
.dynasty-bylaws-changelog li { display: grid; grid-template-columns: 92px 1fr; gap: 12px; padding: 10px 0; border-top: 1px solid var(--border); }
.dynasty-bylaws-changelog span { padding-top: 2px; color: var(--accent); font: .7rem var(--font-mono); }
.dynasty-bylaws-changelog p { margin: 0; color: var(--text2); font-size: .85rem; line-height: 1.55; }
@media (max-width: 640px) {
  .dynasty-bylaws-header { align-items: flex-start; }
  .dynasty-bylaws-hero { grid-template-columns: 1fr; }
  .dynasty-bylaws-hero-mark { width: 46px; height: 46px; }
  .dynasty-bylaws-article h2 { font-size: 1.03rem; }
  .dynasty-bylaws-article h2 > em { margin-left: 0; }
  .dynasty-bylaws-changelog li { grid-template-columns: 1fr; gap: 4px; }
}
`;
